import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { BookOpen, Clock, Award, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CoursesPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, myCoursesRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/courses/my-courses`, { headers: getAuthHeader() })
      ]);
      setCourses(coursesRes.data);
      setMyCourses(myCoursesRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      await axios.post(
        `${API}/courses/enroll?course_id=${courseId}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Enrolled successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
    }
  };

  const isEnrolled = (courseId) => {
    return myCourses.some(c => c.course_id === courseId);
  };

  const filteredCourses = filter === 'all' 
    ? courses 
    : courses.filter(c => c.level === filter);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="courses-page">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Ayurveda Learning
          </h1>
          <p className="text-lg text-gray-600">Master ancient wisdom with modern teaching methods</p>
        </div>

        {/* My Courses Section */}
        {myCourses.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">My Enrolled Courses</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {myCourses.map((enrollment) => (
                <Card key={enrollment.id} className="card-lift">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <h3 className="font-semibold mb-2">{enrollment.course_title}</h3>
                      <div className="flex items-center text-sm text-gray-600">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        {enrollment.progress_percent}% Complete
                      </div>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full mb-4">
                      <div 
                        className="h-full bg-primary rounded-full" 
                        style={{ width: `${enrollment.progress_percent}%` }}
                      />
                    </div>
                    <Button variant="outline" className="w-full">Continue Learning</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Filters */}
        <div className="flex space-x-4 mb-8">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            All Courses
          </Button>
          <Button 
            variant={filter === 'beginner' ? 'default' : 'outline'}
            onClick={() => setFilter('beginner')}
            data-testid="filter-beginner"
          >
            Beginner
          </Button>
          <Button 
            variant={filter === 'intermediate' ? 'default' : 'outline'}
            onClick={() => setFilter('intermediate')}
            data-testid="filter-intermediate"
          >
            Intermediate
          </Button>
          <Button 
            variant={filter === 'advanced' ? 'default' : 'outline'}
            onClick={() => setFilter('advanced')}
            data-testid="filter-advanced"
          >
            Advanced
          </Button>
        </div>

        {/* Course Catalog */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCourses.map((course) => (
            <Card key={course.id} className="card-lift">
              <div className="h-48 bg-muted overflow-hidden">
                <img src={course.image_url} alt={course.title} className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full capitalize">
                    {course.level}
                  </span>
                  <span className="text-xs text-gray-600">{course.category}</span>
                </div>

                <h3 className="text-xl font-semibold mb-2">{course.title}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">{course.description}</p>

                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {course.duration_hours}h
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-1" />
                    {course.enrolled_count} enrolled
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-primary">₹{course.price}</div>
                  <Button
                    onClick={() => handleEnroll(course.id)}
                    disabled={isEnrolled(course.id)}
                    data-testid={`enroll-${course.id}-btn`}
                  >
                    {isEnrolled(course.id) ? 'Enrolled' : 'Enroll Now'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredCourses.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500">No courses found in this category</p>
          </div>
        )}

        {/* Certification Info */}
        <section className="mt-20 bg-primary/5 rounded-2xl p-12 text-center">
          <Award className="h-16 w-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Earn Certificates
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Complete courses to earn recognized Ayurveda certifications. Download and share your achievements.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CoursesPage;